import React, { useMemo } from 'react'

import { useSelector } from 'react-redux'

import { FlexPortionTileBadge } from '@features/recipe-tile'
import type { CompactCampaignBannerHeaderContentBlock } from '@library/api-menu-experience'
import { DeviceType, useDeviceType } from '@library/dom'
import { CompactCampaignBanner, FlexPortionEditorBoundary } from '@library/menu-page'
import type { RecipeUUID } from '@library/menu-service'

import { useMerchandisingBannerBasket } from 'routes/Menu/MenuExperience/hooks/useMerchandisingBannerBasket'
import { useSetBrowserCTAVisibility } from 'routes/Menu/domains/menu'
import { getBasketDate } from 'selectors/basket'

import { useMenuExperienceCategoryNavigation } from '../menuExperienceNavigationContext'
import { useMenuExperienceRecipeDetails } from '../menuExperienceRecipeDetailContext'
import { useRecipeGridCustomisation } from '../recipeGridCustomisationContext'
import { useRecipeGridFlex } from './useRecipeGridFlex'

type CompactCampaignBannerContentBlockWrapperProps = {
  contentBlock: CompactCampaignBannerHeaderContentBlock
}

export const CompactCampaignBannerContentBlockWrapper = ({
  contentBlock,
}: CompactCampaignBannerContentBlockWrapperProps) => {
  const { selectCategory } = useMenuExperienceCategoryNavigation()
  const { menuId, menuUrn, openRecipeDetail } = useMenuExperienceRecipeDetails()
  const deliveryDate = useSelector(getBasketDate)
  const { getRecipeGridItem } = useRecipeGridCustomisation()
  const isMobile = useDeviceType() === DeviceType.MOBILE
  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()
  const displayedContentBlock = useMemo(
    () => ({
      ...contentBlock,
      items: contentBlock.items.map(getRecipeGridItem),
    }),
    [contentBlock, getRecipeGridItem],
  )
  const { canAddRecipes, getRecipeCardState, onAddClick, onRemoveClick, reachedLimit } =
    useMerchandisingBannerBasket(displayedContentBlock)
  const {
    closeFlexPortionEditor,
    getFlexPortionData,
    openFlexPortionEditor,
    removeFlexRecipe,
    selectFlexPortions,
    trackFlexPortionEdit,
    updateFlexPortions,
  } = useRecipeGridFlex({
    items: displayedContentBlock.items,
    removalPosition: 'collection_merchandising_header',
  })

  const onCtaClick = (targetCategory: { id: string; slug: string }) => {
    selectCategory(targetCategory, 'merch_banner', undefined, {
      merch_header_type: contentBlock.type,
    })
  }

  const onRecipeClick = (recipeId: RecipeUUID, initialFlexPortionEditorOpen = false) => {
    const recipe = displayedContentBlock.items.find((item) => item.id === recipeId)

    if (!recipe) {
      return
    }

    openRecipeDetail({
      recipeId,
      recipeUrn: recipe.urn,
      title: recipe.title,
      source: 'merch_banner',
      merchHeaderType: contentBlock.type,
      initialFlexPortionEditorOpen,
    })
  }

  const onEditFlexPortions = (recipeId: RecipeUUID) => {
    if (isMobile) {
      openFlexPortionEditor(recipeId)

      return
    }

    trackFlexPortionEdit(recipeId)
    onRecipeClick(recipeId, true)
  }

  const renderFlexPortionBadge = (recipeId: RecipeUUID) => {
    const { isVisible, quantity, numPortions } = getFlexPortionData(recipeId)

    if (!isVisible) {
      return null
    }

    return (
      <FlexPortionTileBadge
        recipeCount={quantity}
        portionCount={numPortions}
        onEdit={() => onEditFlexPortions(recipeId)}
      />
    )
  }

  const renderFlexPortionEditor = (recipeId: RecipeUUID) => {
    const { isEditorOpen, quantity, numPortions } = getFlexPortionData(recipeId)
    const recipe = displayedContentBlock.items.find((item) => item.id === recipeId)

    if (!isEditorOpen || !recipe) {
      return null
    }

    return (
      <FlexPortionEditorBoundary
        // The endpoint requires mutually exclusive UUID/URN identifiers.
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(recipe.urn ? { recipeUrn: recipe.urn } : { recipeId })}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(menuUrn ? { menuUrn } : { menuId })}
        deliveryDate={deliveryDate}
        isOpen
        recipeCount={quantity}
        numPortions={numPortions}
        onClose={closeFlexPortionEditor}
        onRemoveRecipe={() => removeFlexRecipe(recipeId)}
        onUpdatePortions={(updatedNumPortions) => updateFlexPortions(recipeId, updatedNumPortions)}
        onSelectPortions={(updatedNumPortions) => selectFlexPortions(recipeId, updatedNumPortions)}
        variant="floating-tile"
      />
    )
  }

  return (
    <CompactCampaignBanner
      contentBlock={displayedContentBlock}
      getRecipeCardState={getRecipeCardState}
      canAddRecipes={canAddRecipes}
      reachedLimit={reachedLimit}
      onAddClick={onAddClick}
      onBlockedAddAttempt={setBrowserCTAVisible}
      onCtaClick={onCtaClick}
      onRecipeClick={onRecipeClick}
      onRemoveClick={onRemoveClick}
      renderFlexPortionBadge={renderFlexPortionBadge}
      renderFlexPortionEditor={renderFlexPortionEditor}
    />
  )
}
